import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ImageSquare, Palette, Trash, UploadSimple } from "@phosphor-icons/react";

const asset = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function MeetBranding() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [brandName, setBrandName] = useState("");
  const [brandColor, setBrandColor] = useState("#4f46e5");
  const [busy, setBusy] = useState(false);
  const logoInput = useRef(null);
  const bgInput = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/meet/branding");
      setData(data);
      setBrandName(data.branding.brand_name || "");
      setBrandColor(data.branding.brand_color || "#4f46e5");
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const saveText = async () => {
    setBusy(true);
    try { await api.patch("/meet/branding", { brand_name: brandName, brand_color: brandColor }); toast.success("Branding saved"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data: up } = await api.post("/meet/background", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/meet/branding", { logo_url: up.url });
      toast.success("Logo updated"); await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); if (logoInput.current) logoInput.current.value = ""; }
  };
  const uploadBg = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data: up } = await api.post("/meet/background", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/meet/branding/background", { url: up.url, name: f.name.slice(0, 30) });
      toast.success("Background added"); await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); if (bgInput.current) bgInput.current.value = ""; }
  };
  const removeBg = async (id) => {
    try { await api.delete(`/meet/branding/background/${id}`); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="p-6 text-muted-foreground">Loading…</div>;

  if (!data.can_manage) {
    return (
      <div className="space-y-4" data-testid="branding-no-access">
        <button onClick={() => navigate("/app/meet")} className="flex items-center gap-1.5 text-sm text-muted-foreground"><ArrowLeft size={15} /> Back to meetings</button>
        <div className="rounded-2xl border border-border bg-card p-6">
          <h1 className="font-display text-2xl font-semibold">Meeting branding</h1>
          <p className="mt-2 text-muted-foreground">Only an organization admin can manage meeting branding. Ask your admin to set up your team's logo and backgrounds.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branding-page">
      <button onClick={() => navigate("/app/meet")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> Back to meetings</button>
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Meeting branding</h1>
        <p className="mt-2 text-muted-foreground">Your logo, colour and background library appear across your team's meetings and guest join screens.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><Palette size={20} weight="duotone" /> Identity</h2>
          <label className="mt-4 block text-xs font-semibold text-muted-foreground">Brand name
            <input data-testid="brand-name" value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Your organization" className="mt-1.5 w-full rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary" />
          </label>
          <label className="mt-3 block text-xs font-semibold text-muted-foreground">Accent colour
            <div className="mt-1.5 flex items-center gap-2">
              <input data-testid="brand-color" type="color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)} className="h-10 w-14 rounded-lg border border-input bg-card" />
              <span className="font-mono-metric text-sm">{brandColor}</span>
            </div>
          </label>
          <button data-testid="brand-save" disabled={busy} onClick={saveText} className="mt-4 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Save</button>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><ImageSquare size={20} weight="duotone" /> Logo</h2>
          <div className="mt-4 flex items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted" data-testid="brand-logo-preview">
              {data.branding.logo_url ? <img src={asset(data.branding.logo_url)} alt="logo" className="h-full w-full object-contain" /> : <ImageSquare size={28} className="text-muted-foreground" />}
            </div>
            <div>
              <input ref={logoInput} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="brand-logo-input" />
              <button data-testid="brand-logo-upload" disabled={busy} onClick={() => logoInput.current?.click()} className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-primary disabled:opacity-60"><UploadSimple size={15} /> Upload logo</button>
              <p className="mt-1.5 text-xs text-muted-foreground">PNG or SVG on transparent works best.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><ImageSquare size={20} weight="duotone" /> Background library</h2>
          <input ref={bgInput} type="file" accept="image/*" onChange={uploadBg} className="hidden" data-testid="brand-bg-input" />
          <button data-testid="brand-bg-upload" disabled={busy} onClick={() => bgInput.current?.click()} className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60"><UploadSimple size={15} /> Add background</button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Members can pick these as their meeting or camera background.</p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="brand-bg-grid">
          {(data.backgrounds || []).map((b) => (
            <div key={b.id} data-testid={`brand-bg-${b.id}`} className="group relative aspect-video overflow-hidden rounded-xl border border-border">
              <img src={asset(b.url)} alt={b.name} className="h-full w-full object-cover" />
              <button data-testid={`brand-bg-del-${b.id}`} onClick={() => removeBg(b.id)} className="absolute right-1.5 top-1.5 rounded-full bg-black/60 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"><Trash size={14} /></button>
            </div>
          ))}
          {(!data.backgrounds || data.backgrounds.length === 0) && <p className="col-span-full py-6 text-center text-sm text-muted-foreground">No backgrounds yet.</p>}
        </div>
      </div>
    </div>
  );
}
